const step = (target, title, content, placement = "bottom") => ({
  target,
  header: { title },
  content,
  params: {
    placement,
  },
});

export const buildEmployeesTour = ({
  t,
  canCreate,
  canManage,
  hasItems,
  hasPagination,
  isMobile,
}) => {
  const steps = [
    step(
      '[data-tour="employees-page"]',
      t("tutorials.employees.introduction.title"),
      t("tutorials.employees.introduction.content"),
    ),
    step(
      '[data-tour="employees-filters"]',
      t("tutorials.employees.filters.title"),
      t("tutorials.employees.filters.content"),
    ),
    step(
      isMobile
        ? '[data-tour="employees-mobile-list"]'
        : '[data-tour="employees-table"]',
      t("tutorials.employees.results.title"),
      t(
        isMobile
          ? "tutorials.employees.results.mobileContent"
          : "tutorials.employees.results.desktopContent",
      ),
      "top",
    ),
  ];

  if (hasItems && canManage) {
    steps.push(
      step(
        isMobile
          ? '[data-tour="employees-mobile-actions"]'
          : '[data-tour="employees-desktop-actions"]',
        t("tutorials.employees.actions.title"),
        t("tutorials.employees.actions.content"),
        "left",
      ),
    );
  }

  if (!isMobile && hasPagination) {
    steps.push(
      step(
        '[data-tour="employees-pagination"]',
        t("tutorials.employees.pagination.title"),
        t("tutorials.employees.pagination.content"),
        "top",
      ),
    );
  }

  if (canCreate) {
    steps.push(
      step(
        '[data-tour="employees-create"]',
        t("tutorials.employees.create.title"),
        t("tutorials.employees.create.content"),
        "bottom",
      ),
    );
  }

  return steps;
};

export const buildEmployeeFormTour = ({
  t,
  mode,
  activeTab,
  scheduleMode,
  isMobile,
}) => {
  const isReadOnly = mode === "show";
  const steps = [
    step(
      '[data-tour="employee-form-page"]',
      t(`tutorials.employees.form.${mode}.introductionTitle`),
      t(`tutorials.employees.form.${mode}.introductionContent`),
    ),
  ];

  if (activeTab === 0) {
    steps.push(
      step(
        '[data-tour="employee-form-details"]',
        t("tutorials.employees.form.details.title"),
        t(
          isReadOnly
            ? "tutorials.employees.form.details.readOnlyContent"
            : "tutorials.employees.form.details.content",
        ),
      ),
      step(
        '[data-tour="employee-form-photo"]',
        t("tutorials.employees.form.photo.title"),
        t(
          isReadOnly
            ? "tutorials.employees.form.photo.readOnlyContent"
            : "tutorials.employees.form.photo.content",
        ),
      ),
      step(
        '[data-tour="employee-form-contact"]',
        t("tutorials.employees.form.contact.title"),
        t("tutorials.employees.form.contact.content"),
      ),
      step(
        '[data-tour="employee-form-role"]',
        t("tutorials.employees.form.role.title"),
        t("tutorials.employees.form.role.content"),
      ),
      step(
        '[data-tour="employee-form-schedule-type"]',
        t("tutorials.employees.form.scheduleType.title"),
        t("tutorials.employees.form.scheduleType.content"),
      ),
    );
  } else {
    steps.push(
      step(
        '[data-tour="employee-schedule-tab"]',
        t("tutorials.employees.form.schedule.title"),
        t("tutorials.employees.form.schedule.content"),
      ),
    );

    if (scheduleMode === "weekly") {
      steps.push(
        step(
          '[data-tour="employee-weekly-schedule"]',
          t("tutorials.employees.form.weekly.editorTitle"),
          t(
            isReadOnly
              ? "tutorials.employees.form.weekly.editorReadOnlyContent"
              : "tutorials.employees.form.weekly.editorContent",
          ),
        ),
        step(
          isMobile
            ? '[data-tour="employee-weekly-mobile-list"]'
            : '[data-tour="employee-weekly-table"]',
          t("tutorials.employees.form.weekly.resultsTitle"),
          t(
            isMobile
              ? "tutorials.employees.form.weekly.resultsMobileContent"
              : "tutorials.employees.form.weekly.resultsDesktopContent",
          ),
          "top",
        ),
      );
    } else if (scheduleMode === "calendar") {
      steps.push(
        step(
          '[data-tour="employee-calendar-schedule"]',
          t("tutorials.employees.form.calendar.title"),
          t("tutorials.employees.form.calendar.content"),
          "top",
        ),
      );
    } else if (scheduleMode === "none") {
      steps.push(
        step(
          '[data-tour="employee-no-schedule"]',
          t("tutorials.employees.form.noSchedule.title"),
          t("tutorials.employees.form.noSchedule.content"),
        ),
      );
    } else {
      steps.push(
        step(
          '[data-tour="employee-select-schedule"]',
          t("tutorials.employees.form.selectSchedule.title"),
          t("tutorials.employees.form.selectSchedule.content"),
        ),
      );
    }
  }

  steps.push(
    step(
      '[data-tour="employee-form-navigation"]',
      t("tutorials.employees.form.navigation.title"),
      t(
        activeTab === 0
          ? "tutorials.employees.form.navigation.detailsContent"
          : isReadOnly
            ? "tutorials.employees.form.navigation.readOnlyScheduleContent"
            : "tutorials.employees.form.navigation.scheduleContent",
      ),
      "top",
    ),
    step(
      '[data-tour="employee-form-back"]',
      t("tutorials.employees.form.back.title"),
      t("tutorials.employees.form.back.content"),
      "bottom",
    ),
  );

  return steps;
};

export const buildEmployeeWorkCalendarTour = ({
  t,
  disabled,
  hasSelectedDate,
  hasScheduledItems,
  hasPagination,
}) => {
  const steps = [
    step(
      '[data-tour="work-calendar-page"]',
      t("tutorials.employees.workCalendar.introduction.title"),
      t(
        disabled
          ? "tutorials.employees.workCalendar.introduction.readOnlyContent"
          : "tutorials.employees.workCalendar.introduction.content",
      ),
    ),
    step(
      '[data-tour="work-calendar-calendar"]',
      t("tutorials.employees.workCalendar.calendar.title"),
      t("tutorials.employees.workCalendar.calendar.content"),
      "top",
    ),
    step(
      '[data-tour="work-calendar-editor"]',
      t("tutorials.employees.workCalendar.editor.title"),
      t(
        hasSelectedDate
          ? disabled
            ? "tutorials.employees.workCalendar.editor.selectedReadOnlyContent"
            : "tutorials.employees.workCalendar.editor.selectedContent"
          : "tutorials.employees.workCalendar.editor.emptyContent",
      ),
      "left",
    ),
    step(
      '[data-tour="work-calendar-scheduled"]',
      t("tutorials.employees.workCalendar.scheduled.title"),
      t(
        hasScheduledItems
          ? "tutorials.employees.workCalendar.scheduled.content"
          : "tutorials.employees.workCalendar.scheduled.emptyContent",
      ),
      "top",
    ),
  ];

  if (hasPagination) {
    steps.push(
      step(
        '[data-tour="work-calendar-pagination"]',
        t("tutorials.employees.workCalendar.pagination.title"),
        t("tutorials.employees.workCalendar.pagination.content"),
        "top",
      ),
    );
  }

  return steps;
};
